import type { CSSProperties } from 'react';
import { CreditCard } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { TableEmptyRow } from '@/components/TableEmptyRow';
import { CardNo, MemberLink } from '@/components/links/RecordLinks';
import { cn, displayMemberNo } from '@/lib/utils';
import type { PaymentMemberRow } from '@/types';
import type { PolicyRow, TableColumn } from './TableColumns';

interface PaymentTableProps {
  rows: PaymentMemberRow[];
  columns: TableColumn[];
  /** Members whose other policies are shown under their row. */
  openIds: Set<string>;
  enteringId: string | null;
  leavingId: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

/** First policy = the member row; the other policies follow it when the member is open. */
const toPolicyRows = (row: PaymentMemberRow, isOpen: boolean): PolicyRow[] => {
  const visible = isOpen ? row.policies : row.policies.slice(0, 1);
  return visible.map((policy, index) => ({
    member: row.member,
    policy,
    balance: row.balance,
    isMemberRow: index === 0,
    position: index + 1,
    policyCount: row.policies.length,
    isOpen,
    isLastChild: index === row.policies.length - 1,
  }));
};

export function PaymentTable({
  rows,
  columns,
  openIds,
  enteringId,
  leavingId,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: PaymentTableProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-hide group-data-[table-locked=true]/page:overflow-y-hidden">
        <Table containerClassName="overflow-visible">
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur">
            <TableRow className="border-gray-200 hover:bg-transparent">
              {columns.map((column) => (
                <TableHead key={column.id} className={cn(column.className, 'h-11 px-4 text-left align-middle')}>
                  {column.renderHeader()}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.length === 0 && <TableEmptyRow columnCount={columns.length} icon={CreditCard} title="No members found" />}
            {rows.map((row) =>
              row.policies.length === 0 ? (
                <NoPolicyRow key={row.member.id} row={row} columnCount={columns.length} />
              ) : (
                toPolicyRows(row, openIds.has(row.member.id)).map((policyRow) => {
                  const animate = !policyRow.isMemberRow && (
                    row.member.id === enteringId ? 'policy-row-enter' : row.member.id === leavingId ? 'policy-row-leave' : ''
                  );
                  return (
                    <TableRow
                      key={policyRow.policy.id}
                      className={cn(
                        'border-gray-100 transition-colors',
                        policyRow.isMemberRow
                          ? cn('hover:bg-gray-50/80', policyRow.isOpen && 'bg-[#4369a0]/[0.03]')
                          : 'bg-gray-50/40',
                        animate
                      )}
                      style={{ '--row-delay': `${(policyRow.position - 2) * 40}ms` } as CSSProperties}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.id} className={cn(column.className, 'px-4 py-3 align-middle')}>
                          {column.renderCell(policyRow)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        itemLabel="members"
      />
    </div>
  );
}

/** A member who has no policy yet: policies are added from the Members page. */
function NoPolicyRow({ row, columnCount }: { row: PaymentMemberRow; columnCount: number }) {
  const { member } = row;
  return (
    <TableRow className="border-gray-100">
      <TableCell className="px-4 py-3">
        <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 text-sm font-medium">{displayMemberNo(member)}</span>
      </TableCell>
      <TableCell className="px-4 py-3 text-[13px]">
        <CardNo value={member.cardNo} />
      </TableCell>
      <TableCell className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[#4369a0]/10 text-xs font-semibold text-[#4369a0]">
              {member.name.split(' ').map((part) => part[0]).join('').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <MemberLink memberId={member.id} memberNo={member.memberNo} name={member.name} className="text-sm font-medium text-gray-900" />
            <p className="text-xs text-gray-500">{member.phone}</p>
          </div>
        </div>
      </TableCell>
      <TableCell colSpan={columnCount - 3} className="px-4 py-3 text-sm italic text-gray-500">
        No policy yet. Add a policy from the Members page.
      </TableCell>
    </TableRow>
  );
}
